import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';

import Link from './../../components/Link';
import AdminPageShell from '../../components/admin/AdminPageShell';
import api from './../../lib/api';
import { getSportNames } from '../../lib/sportUtils';
import { approveSportComplex, rejectSportComplex, fetchCenters } from '../../ducks/centers';

class SportCenters extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    centers: PropTypes.array.isRequired,
    classes: PropTypes.object.isRequired,
    userRole: PropTypes.string,
    fetchCenters: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      cityFilter: 'all',
      processing: new Set(),
      previewCache: {},
      previewLoadingId: null,
      previewAnchor: null,
    };
  }

  componentDidMount() {
    if ((!this.props.centers || this.props.centers.length === 0) && this.props.fetchCenters) {
      this.props.fetchCenters().catch(() => {});
    }
  }

  handlePreviewOpen = (e, center) => {
    e.stopPropagation();
    const anchorEl = e.currentTarget;
    const id = center.id;
    if (this.state.previewCache[id]) {
      this.setState({ previewAnchor: { id, el: anchorEl } });
      return;
    }
    this.setState({ previewLoadingId: id, previewAnchor: { id, el: anchorEl } });
    api.getModerationPreview(id)
      .then((data) => {
        this.setState((prev) => ({
          previewCache: { ...prev.previewCache, [id]: data },
          previewLoadingId: prev.previewLoadingId === id ? null : prev.previewLoadingId,
        }));
      })
      .catch(() => {
        this.setState((prev) => (prev.previewLoadingId === id ? { previewLoadingId: null } : {}));
      });
  };

  handlePopoverClose = () => {
    this.setState({ previewAnchor: null });
  };

  redirectToCenter = id => this.props.history.push(`/center/${id}`);

  getStatusLabel = (status) => {
    // Статус может приходить как строка ("Pending", "Approved", "Rejected") или число (0, 1, 2)
    const statusValue = typeof status === 'string' ? status : status;
    
    if (statusValue === 'Pending' || statusValue === 0 || statusValue === '0') {
      return 'На проверке';
    }
    if (statusValue === 'Approved' || statusValue === 1 || statusValue === '1') {
      return 'Одобрен';
    }
    if (statusValue === 'Rejected' || statusValue === 2 || statusValue === '2') {
      return 'Отклонен';
    }
    return 'Неизвестно';
  };

  getStatusColor = (status) => {
    // Статус может приходить как строка ("Pending", "Approved", "Rejected") или число (0, 1, 2)
    const statusValue = typeof status === 'string' ? status : status;
    
    if (statusValue === 'Pending' || statusValue === 0 || statusValue === '0') {
      return 'default'; // На проверке - серый
    }
    if (statusValue === 'Approved' || statusValue === 1 || statusValue === '1') {
      return 'primary'; // Одобрен - синий
    }
    if (statusValue === 'Rejected' || statusValue === 2 || statusValue === '2') {
      return 'secondary'; // Отклонен - красный
    }
    return 'default';
  };

  handleApprove = async (e, id) => {
    e.stopPropagation();
    this.setState(prev => ({
      processing: new Set(prev.processing).add(id),
    }));
    try {
      await this.props.approveSportComplex(id);
    } catch (err) {
      console.error('Ошибка одобрения комплекса:', err);
      alert(err.message || 'Ошибка при одобрении комплекса');
    } finally {
      this.setState(prev => {
        const newSet = new Set(prev.processing);
        newSet.delete(id);
        return { processing: newSet };
      });
    }
  };

  handleReject = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Вы уверены, что хотите отклонить этот спортивный комплекс?')) {
      return;
    }
    this.setState(prev => ({
      processing: new Set(prev.processing).add(id),
    }));
    try {
      await this.props.rejectSportComplex(id);
    } catch (err) {
      console.error('Ошибка отклонения комплекса:', err);
      alert(err.message || 'Ошибка при отклонении комплекса');
    } finally {
      this.setState(prev => {
        const newSet = new Set(prev.processing);
        newSet.delete(id);
        return { processing: newSet };
      });
    }
  };

  render() {
    const { centers, classes, userRole } = this.props;
    const { processing } = this.state;
    
    // Получаем уникальные города, фильтруя undefined/null центры
    const uniqueCities = _.uniq(centers
      .filter(center => center && center.city) // Фильтруем undefined/null центры и центры без city
      .map(center => center.city)
      .filter(city => city));
    
    // Фильтруем центры по городу
    let filteredCenters = centers;
    if (this.state.cityFilter !== 'all') {
      filteredCenters = centers.filter(center => center.city === this.state.cityFilter);
    }
    
    // Сортируем: сначала Pending (на модерации), затем остальные
    // Бэкенд отдаёт Status в PascalCase, поэтому проверяем оба варианта
    const getStatus = (center) => center.status !== undefined ? center.status : center.Status;
    filteredCenters = _.orderBy(filteredCenters, [
      (center) => {
        const status = getStatus(center);
        const value = status !== undefined && status !== null ? status : 'Pending';
        // Pending (0 или 'Pending') получают приоритет 0, остальные - 1
        if (value === 'Pending' || value === 0 || value === '0') {
          return 0;
        }
        return 1;
      },
      'name' // Вторичная сортировка по названию
    ], ['asc', 'asc']);
    
    const isAdmin = userRole === 'administrator';
    const isModerator = userRole === 'moderator';
    
    return (
      <AdminPageShell title="Спортивные центры">
        <div className={classes.pageBody}>
        <div className={classes.buttonBar}>
          <Select
            value={this.state.cityFilter}
            onChange={event => this.setState({ cityFilter: event.target.value })}
            input={<Input id="city-filter" />}
            style={{ marginRight: '16px' }}
          >
            <MenuItem value="all">
              <em>Все города</em>
            </MenuItem>
            {uniqueCities.map(city => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </Select>
          <Button to="/new-center" variant="contained" color="primary" component={Link} className={classes.button}>
            Новый спортцентр
          </Button>
        </div>
        <Paper className={classes.paper} elevation={2}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Город</TableCell>
                <TableCell>Название</TableCell>
                <TableCell>Адрес</TableCell>
                {(isModerator || isAdmin) && <TableCell>Статус</TableCell>}
                {isAdmin && <TableCell>Действия</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCenters.map(n => {
                const status = n.status !== undefined ? n.status : (n.Status !== undefined ? n.Status : 'Pending');
                const isProcessing = processing.has(n.id);
                
                const showPreview = this.props.userRole === 'administrator' && (status === 'Pending' || status === 0 || status === '0' || status === 'Rejected' || status === 2 || status === '2');
                return (
                  <TableRow
                    className={classes.tableRow}
                    onClick={() => !isProcessing && this.redirectToCenter(n.id)}
                    key={n.id}
                  >
                    <TableCell>
                      {n.city || 'Не указан'}
                    </TableCell>
                    <TableCell>
                      {n.name}
                    </TableCell>
                    <TableCell>
                      {n.address}
                    </TableCell>
                    {(isModerator || isAdmin) && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className={classes.statusCell}>
                          {showPreview && (
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.previewTriggerBtn}
                              onClick={(e) => this.handlePreviewOpen(e, n)}
                            >
                              Превью
                            </Button>
                          )}
                          <Chip
                            label={this.getStatusLabel(status)}
                            color={this.getStatusColor(status)}
                            size="small"
                          />
                        </div>
                      </TableCell>
                    )}
                    {isAdmin && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <div className={classes.actionButtons}>
                          {(status === 'Pending' || status === 0 || status === '0') && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={(e) => this.handleApprove(e, n.id)}
                                disabled={isProcessing}
                                style={{ marginRight: 8 }}
                              >
                                Одобрить
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                onClick={(e) => this.handleReject(e, n.id)}
                                disabled={isProcessing}
                              >
                                Отклонить
                              </Button>
                            </>
                          )}
                          {(status === 'Rejected' || status === 2 || status === '2') && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={(e) => this.handleApprove(e, n.id)}
                              disabled={isProcessing}
                            >
                              Одобрить
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
        {this.props.userRole === 'administrator' && (
          <Popover
            open={!!(this.state.previewAnchor && this.state.previewAnchor.el)}
            anchorEl={this.state.previewAnchor?.el || null}
            onClose={this.handlePopoverClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            ModalProps={{
              hideBackdrop: true,
              disableScrollLock: true,
              disableEnforceFocus: true,
              disableAutoFocus: true,
              style: { pointerEvents: 'none' },
            }}
            PaperProps={{
              className: classes.previewPopover,
              style: { pointerEvents: 'auto' },
            }}
          >
            <div className={classes.previewContent}>
              {(() => {
                const center = this.props.centers.find(c => c.id === this.state.previewAnchor?.id);
                const centerName = center ? (center.name || 'Спорткомплекс') : 'Спорткомплекс';
                const centerCity = center ? (center.city || '') : '';
              return (
                <>
                  <div className={classes.previewHeader}>
                    <Typography className={classes.previewTitle}>{centerName}</Typography>
                    {centerCity && (
                      <Typography className={classes.previewSubtitle}>{centerCity}</Typography>
                    )}
                  </div>
                  {this.state.previewLoadingId === this.state.previewAnchor?.id ? (
                    <Typography color="textSecondary" className={classes.previewLoading}>Загрузка…</Typography>
                  ) : (() => {
                    const data = this.state.previewCache[this.state.previewAnchor?.id];
                    if (!data) return null;
                    const { playgrounds = [], meetings = [] } = data;
                    return (
                      <>
                        <Typography variant="subtitle2" className={classes.previewSection}>Площадки</Typography>
                        {playgrounds.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">Нет площадок</Typography>
                        ) : (
                          <ul className={classes.previewList}>
                            {playgrounds.map((p) => (
                              <li key={p.id}>
                                {p.name}
                                {p.sport && p.sport.length > 0 && (
                                  <span className={classes.previewSport}> — {getSportNames(p.sport).join(', ')}</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        <Typography variant="subtitle2" className={classes.previewSection}>Встречи</Typography>
                        {meetings.length === 0 ? (
                          <Typography variant="body2" color="textSecondary">Нет встреч</Typography>
                        ) : (
                          <ul className={classes.previewList}>
                            {meetings.slice(0, 15).map((m) => (
                              <li key={m.id}>
                                {m.playgroundName} · {moment(m.dateTimeStart).format('D MMM, HH:mm')}
                                {m.sport != null && <span className={classes.previewSport}> · {getSportNames([m.sport]).join(', ')}</span>}
                              </li>
                            ))}
                            {meetings.length > 15 && (
                              <li className={classes.previewMore}>… ещё {meetings.length - 15}</li>
                            )}
                          </ul>
                        )}
                      </>
                    );
                  })()}
                  <div className={classes.previewFooter}>
                    <Button size="small" variant="contained" onClick={this.handlePopoverClose}>
                      Закрыть
                    </Button>
                  </div>
                </>
              );
              })()}
            </div>
          </Popover>
        )}
        </div>
      </AdminPageShell>
    );
  }
}

const styles = (theme) => ({
  pageBody: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
  },
  paper: {
    width: '100%',
    overflowX: 'auto',
    flex: 1,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
  },
  buttonBar: {
    height: 67,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  tableRow: {
    cursor: 'pointer',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
  },
  table: {
    '& thead th': {
      backgroundColor: '#f8f9fa',
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#495057',
      borderBottom: '2px solid #dee2e6',
    },
    '& tbody tr': {
      transition: 'background-color 0.2s ease',
      '&:hover': {
        backgroundColor: '#f8f9fa',
      },
    },
    '& tbody td': {
      borderBottom: '1px solid #e9ecef',
      padding: theme.spacing(1.5),
    },
  },
  button: {
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    '& > * + *': { marginLeft: theme.spacing(1) },
  },
  previewTriggerBtn: {
    textTransform: 'none',
    minWidth: 'auto',
    padding: '2px 8px',
    fontSize: '0.75rem',
    lineHeight: 1.25,
  },
  previewFooter: {
    padding: theme.spacing(1.5, 2),
    borderTop: '1px solid #e9ecef',
    background: '#f8f9fa',
  },
  previewPopover: {
    maxWidth: 380,
    maxHeight: 420,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e9ecef',
    overflow: 'hidden',
  },
  previewContent: {
    padding: 0,
    outline: 'none',
  },
  previewHeader: {
    padding: theme.spacing(2, 2, 1.5),
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    borderBottom: '1px solid #dee2e6',
  },
  previewTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#212529',
    lineHeight: 1.3,
  },
  previewSubtitle: {
    fontSize: '0.8125rem',
    color: '#6c757d',
    marginTop: 2,
  },
  previewLoading: {
    padding: theme.spacing(2),
  },
  previewSection: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#6c757d',
    '&:first-of-type': { marginTop: theme.spacing(1.5) },
  },
  previewList: {
    margin: 0,
    padding: theme.spacing(0, 2, 1.5),
    paddingLeft: theme.spacing(3),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: '#495057',
  },
  previewSport: {
    color: '#868e96',
    fontWeight: 400,
  },
  previewMore: {
    fontStyle: 'italic',
    color: '#868e96',
    fontSize: '0.8125rem',
  },
});

const Styled = withStyles(styles)(SportCenters);
export default connect(
  state => ({ centers: state.centers, userRole: state.auth.role }),
  { approveSportComplex, rejectSportComplex, fetchCenters },
)(Styled);
